import { arrayChunk, createDebouncer, request } from '.';
import { pDefer } from './promise';

const { STOREFRONT_ACCESS_TOKEN } = window;

const STOREFRONT_PRODUCTS_QUERY = /* GraphQL */ `
  fragment ProductFields on Product {
    id
    title
    handle
    tags
    onlineStoreUrl
    availableForSale
    productType

    options {
      id
      name
      values
    }

    priceRange {
      maxVariantPrice {
        amount
        currencyCode
      }
      minVariantPrice {
        amount
        currencyCode
      }
    }

    compareAtPriceRange {
      maxVariantPrice {
        amount
        currencyCode
      }
      minVariantPrice {
        amount
        currencyCode
      }
    }

    brand: metafield(namespace: "custom", key: "brand") {
      value
    }
    gwpProduct: metafield(namespace: "custom", key: "gwp_product") {
      value
      reference {
        ... on ProductVariant {
          id
          title
          price {
            amount
            currencyCode
          }
          availableForSale
          product {
            id
            title
            onlineStoreUrl
            images(first: 1) {
              nodes {
                id
                altText
                url
                width
                height
              }
            }
          }
        }
      }
    }
    gwpProductAmount: metafield(namespace: "custom", key: "collection_gwp_amount") {
      value
    }
    gwpProductMessage: metafield(namespace: "custom", key: "gwp_pdp_message") {
      value
    }

    description: metafield(namespace: "custom", key: "description") {
      value
    }

    print: metafield(namespace: "custom", key: "print") {
      value
    }

    qualifiers: metafield(namespace: "custom", key: "qualifiers") {
      value
    }

    type: metafield(namespace: "custom", key: "type") {
      value
    }

    fit: metafield(namespace: "custom", key: "fit") {
      value
    }

    availableCountryToSell: metafield(namespace: "custom", key: "available_country_to_sell") {
      value
    }

    customPdpRedCopy: metafield(namespace: "custom", key: "custom_pdp_red_copy") {
      value
    }

    customPlpRedCopy: metafield(namespace: "custom", key: "custom_plp_red_copy") {
      value
    }

    pdpCopyThemeState: metafield(namespace: "custom", key: "pdp_copy_theme_state_") {
      value
    }

    plpCopyThemeState: metafield(namespace: "custom", key: "plp_copy_theme_state_") {
      value
    }

    comingSoonListId: metafield(
      namespace: "custom"
      key: "coming_soon_list_id"
    ) {
      value
    }

    seasonalCore: metafield(namespace: "custom", key: "seasonal_core") {
      value
    }

    division: metafield(namespace: "custom", key: "division") {
      value
    }

    size_guide_type: metafield(namespace: "custom", key: "size_guide_type") {
      value
    }

    launchDate: metafield(namespace: "custom", key: "launch_date") {
      value
    }
    groupedProducts: metafield(namespace: "custom", key: "grouped_products") {
      value
    }

    bundleProducts: metafield(namespace: "custom", key: "bundle_products") {
      value
    }

    swatchGroup: metafield(namespace: "custom", key: "swatch_group_name") {
      value
    }

    images(first: 25) {
      nodes {
        id
        altText
        url
        width
        height
      }
    }

    variants(first: 250) {
      nodes {
        id
        sku
        quantityAvailable
        availableForSale
        currentlyNotInStock

        pdpCopy: metafield(namespace: "custom", key: "pdp_copy") {
          value
        }

        isBackorder: metafield(namespace: "custom", key: "is_backorder") {
          value
        }

        backOrderShippingDate: metafield(namespace: "custom", key: "backorder_shipping_date") {
          value
        }

        price {
          amount
          currencyCode
        }

        compareAtPrice {
          amount
          currencyCode
        }

        selectedOptions {
          name
          value
        }
      }
    }
  }

  query Products(
    $country: CountryCode!
    $id1: ID!
    $include2: Boolean!
    $id2: ID
    $include3: Boolean!
    $id3: ID
    $include4: Boolean!
    $id4: ID
    $include5: Boolean!
    $id5: ID
    $include6: Boolean!
    $id6: ID
    $include7: Boolean!
    $id7: ID
    $include8: Boolean!
    $id8: ID
    $include9: Boolean!
    $id9: ID
    $include10: Boolean!
    $id10: ID
  ) @inContext(country: $country) {
    p1: product(id: $id1) {
      ...ProductFields
    }
    p2: product(id: $id2) @include(if: $include2) {
      ...ProductFields
    }
    p3: product(id: $id3) @include(if: $include3) {
      ...ProductFields
    }
    p4: product(id: $id4) @include(if: $include4) {
      ...ProductFields
    }
    p5: product(id: $id5) @include(if: $include5) {
      ...ProductFields
    }
    p6: product(id: $id6) @include(if: $include6) {
      ...ProductFields
    }
    p7: product(id: $id7) @include(if: $include7) {
      ...ProductFields
    }
    p8: product(id: $id8) @include(if: $include8) {
      ...ProductFields
    }
    p9: product(id: $id9) @include(if: $include9) {
      ...ProductFields
    }
    p10: product(id: $id10) @include(if: $include10) {
      ...ProductFields
    }
  }
`;

const STOREFRONT_COLLECTIONS_QUERY = /* GraphQL */ `
  fragment CollectionFields on Collection {
    id
    title
    handle

    iconImage: metafield(namespace: "my_fields", key: "collection_icon_image") {
      value

      reference {
        ... on MediaImage {
          alt

          image {
            altText
            url
          }
        }
      }
    }

    iconTitle: metafield(namespace: "my_fields", key: "collection_icon_title") {
      value
    }

    videosList: metafield(namespace: "custom", key: "videos_list") {
      value
    }
  }

  query Collections(
    $country: CountryCode!
    $id1: ID!
    $include2: Boolean!
    $id2: ID
    $include3: Boolean!
    $id3: ID
    $include4: Boolean!
    $id4: ID
    $include5: Boolean!
    $id5: ID
    $include6: Boolean!
    $id6: ID
    $include7: Boolean!
    $id7: ID
    $include8: Boolean!
    $id8: ID
    $include9: Boolean!
    $id9: ID
    $include10: Boolean!
    $id10: ID
  ) @inContext(country: $country) {
    c1: collection(id: $id1) {
      ...CollectionFields
    }
    c2: collection(id: $id2) @include(if: $include2) {
      ...CollectionFields
    }
    c3: collection(id: $id3) @include(if: $include3) {
      ...CollectionFields
    }
    c4: collection(id: $id4) @include(if: $include4) {
      ...CollectionFields
    }
    c5: collection(id: $id5) @include(if: $include5) {
      ...CollectionFields
    }
    c6: collection(id: $id6) @include(if: $include6) {
      ...CollectionFields
    }
    c7: collection(id: $id7) @include(if: $include7) {
      ...CollectionFields
    }
    c8: collection(id: $id8) @include(if: $include8) {
      ...CollectionFields
    }
    c9: collection(id: $id9) @include(if: $include9) {
      ...CollectionFields
    }
    c10: collection(id: $id10) @include(if: $include10) {
      ...CollectionFields
    }
  }
`;

const { Shopify } = window;

const priceFormatters = {};
const getPriceFormatter = (currency) => {
  if (!priceFormatters[currency]) {
    priceFormatters[currency] = new Intl.NumberFormat(Shopify.locale, {
      style: 'currency',
      currency,
    });
  }
  return priceFormatters[currency];
};

export const formatPrice = (price, ignoreZeros = false) => {
  if (ignoreZeros && Number(price.amount) === 0) {
    return '';
  }

  const priceFormatter = getPriceFormatter(price.currencyCode);
  return priceFormatter.format(price.amount);
};

export const formatPriceRange = (min, max, ignoreZeros = false) => {
  if (min.amount === max.amount) {
    return formatPrice(min, ignoreZeros);
  }

  const priceFormatter = getPriceFormatter(min.currencyCode);
  if (priceFormatter.formatRange) {
    return priceFormatter.formatRange(min.amount, max.amount);
  }
  return `${priceFormatter.format(min.amount)} - ${priceFormatter.format(
    max.amount
  )}`;
};

const formatProductPrices = (product) => {
  product.priceRangeFormatted = formatPriceRange(
    product.priceRange.minVariantPrice,
    product.priceRange.maxVariantPrice
  );
  product.compareAtPriceRangeFormatted = formatPriceRange(
    product.compareAtPriceRange.minVariantPrice,
    product.compareAtPriceRange.maxVariantPrice,
    true
  );
  product.variants.forEach((variant) => {
    variant.priceFormatted = formatPrice(variant.price);
    if (variant.compareAtPrice) {
      variant.compareAtPriceFormatted = formatPrice(
        variant.compareAtPrice,
        true
      );
    }
  });
};

const fetchDataBackground = async (
  ids,
  query,
  dataObject,
  inProgressDataObject,
  processItemFunction
) => {
  if (ids.size === 0) {
    // eslint-disable-next-line no-console
    console.warn('No data to fetch');
    return;
  }

  const chunks = arrayChunk([...ids], 10);

  await Promise.all(
    chunks.map(async (chunk) => {
      const variables = new Array(10).fill().reduce((acc, _, index) => {
        const id = chunk[index];
        acc[`id${index + 1}`] = id;
        acc[`include${index + 1}`] = !!id;
        return acc;
      }, {});
      variables.country = Shopify.country;

      let data, error;
      try {
        const res = await fetch(
          `https://${Shopify.shop}/api/2023-07/graphql.json`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
            },
            body: JSON.stringify({ query, variables }),
          }
        );

        const { data: d, errors } = await res.json();
        data = d;

        if (errors) {
          throw new Error(
            `Failed to fetch: ${errors
              .map((error) => error.message)
              .join(', ')}`
          );
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(error);
        error = err;
      }

      if (!error) {
        Object.values(data).filter(Boolean).forEach(processItemFunction);
      }

      chunk.forEach((id) => {
        const product = dataObject[id];

        if (product) {
          inProgressDataObject[id].resolve(product);
        } else {
          const err = error || new Error(`${id} not found`);
          inProgressDataObject[id].reject(err);
        }

        delete inProgressDataObject[id];
      });
    })
  );
};

const fetchData = async (
  ids,
  prefix,
  idsToFetchSet,
  dataObject,
  inProgressDataObject,
  fetchBackgroundFunction
) => {
  const normalizedIds = ids
    .map((id) => {
      if (typeof id === 'string' && id.startsWith(prefix)) {
        return id;
      }

      if (
        typeof id === 'number' ||
        (typeof id === 'string' && /^\d+$/.test(id))
      ) {
        return `${prefix}${id}`;
      }
    })
    .filter(Boolean);

  const idsToFetch = normalizedIds.filter(
    (id) => !dataObject[id] && !inProgressDataObject[id]
  );

  idsToFetch.forEach((id) => {
    idsToFetchSet.add(id);
    inProgressDataObject[id] = pDefer();
  });

  const idsInProgress = normalizedIds.filter((id) => inProgressDataObject[id]);

  if (idsToFetch.length > 0) {
    fetchBackgroundFunction();
  }

  if (idsInProgress.length > 0) {
    await Promise.allSettled(
      idsInProgress.map((id) => inProgressDataObject[id].promise)
    );
  }

  return dataObject;
};

// #region products

export const productsData = {};
export const variantsData = {};

let productsToFetch = new Set();
const productsInProgress = {};

const fetchProductsBackground = async () => {
  const productsToFetchSet = productsToFetch;
  productsToFetch = new Set();

  return fetchDataBackground(
    productsToFetchSet,
    STOREFRONT_PRODUCTS_QUERY,
    productsData,
    productsInProgress,
    (product) => {
      product.variants = product.variants.nodes;
      formatProductPrices(product);
      productsData[product.id] = product;
      productsData[product.id.replace('gid://shopify/Product/', '')] = product;
      product.variants.forEach((variant) => {
        variant.product = product;
        variantsData[variant.id] = variant;
        variantsData[variant.id.replace('gid://shopify/ProductVariant/', '')] =
          variant;
      });
    }
  );
};

const fetchProductsBackgroundDebounced = createDebouncer((cb) =>
  setTimeout(cb, 0)
)(fetchProductsBackground);

export const fetchProducts = async (ids) => {
  // id can be number or gid://shopify/Product/... string

  return fetchData(
    ids,
    'gid://shopify/Product/',
    productsToFetch,
    productsData,
    productsInProgress,
    fetchProductsBackgroundDebounced
  );
};

// #endregion products

// #region products reviews
export const productsReviewsData = {};
let productsReviewsToFetch = new Set();
const productsReviewsInProgress = {};

const fetchProductsReviewsBackground = async () => {
  const ids = productsReviewsToFetch;
  productsReviewsToFetch = new Set();

  if (ids.size === 0) {
    return;
  }

  const idsArray = Array.from(ids);

  if (window.Shopify.shop !== 'poshpeanuts-com.myshopify.com') {
    const err = new Error(`store not supported`);
    idsArray.forEach((id) => {
      productsReviewsInProgress[id].reject(err);
      delete productsReviewsInProgress[id];
    });
    return;
  }

  let error;
  try {
    const data = await request('https://stamped.io/api/widget/badges', {
      method: 'POST',
      body: JSON.stringify({
        productIds: idsArray
          .map((fullId) => {
            const id = fullId.replace('gid://shopify/Product/', '');
            const productData = productsData[id];
            if (!productData) return null;
            return {
              productId: id,
              productSKU: productData.variants[0].sku,
              productTitle: productData.title,
              productType: productData.productType,
            };
          })
          .filter(Boolean),
        apiKey: 'pubkey-6Sf552Y74lgGLEkqtHN5i760pfJ3TJ',
        storeUrl: window.Shopify.shop,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    data.forEach((productData) => {
      const idNormalized = `gid://shopify/Product/${productData.productId}`;
      productsReviewsData[idNormalized] = productData;
    });
  } catch (e) {
    error = e;
  }

  idsArray.forEach((id) => {
    if (productsReviewsData[id]) {
      productsReviewsInProgress[id].resolve(productsReviewsData[id]);
    } else {
      const err = error || new Error(`${id} not found`);
      productsReviewsInProgress[id].reject(err);
    }

    delete productsReviewsInProgress[id];
  });
};

const fetchProductsReviewsBackgroundDebounced = createDebouncer((cb) =>
  setTimeout(cb, 0)
)(fetchProductsReviewsBackground);

export const fetchProductsReviews = async (ids) => {
  // id can be number or gid://shopify/Product/... string

  return fetchData(
    ids,
    'gid://shopify/Product/',
    productsReviewsToFetch,
    productsReviewsData,
    productsReviewsInProgress,
    fetchProductsReviewsBackgroundDebounced
  );
};

// #endregion products reviews

// #region collections

export const collectionsData = {};

let collectionsToFetch = new Set();
const collectionsInProgress = {};

const fetchCollectionsBackground = async () => {
  const collectionsToFetchSet = collectionsToFetch;
  collectionsToFetch = new Set();

  return fetchDataBackground(
    collectionsToFetchSet,
    STOREFRONT_COLLECTIONS_QUERY,
    collectionsData,
    collectionsInProgress,
    (collection) => {
      collectionsData[collection.id] = collection;
      collectionsData[collection.id.replace('gid://shopify/Collection/', '')] =
        collection;
    }
  );
};

const fetchCollectionsBackgroundDebounced = createDebouncer((cb) =>
  setTimeout(cb, 0)
)(fetchCollectionsBackground);

export const fetchCollections = async (ids) => {
  // id can be number or gid://shopify/Collection/... string

  return fetchData(
    ids,
    'gid://shopify/Collection/',
    collectionsToFetch,
    collectionsData,
    collectionsInProgress,
    fetchCollectionsBackgroundDebounced
  );
};

// #endregion collections
